/**
 * BrandBar — the persistent brand strip for TAB ROOT screens (mobile):
 * lockup · spacer · search · bell · arch-avatar. Home renders its own richer
 * greeting band; Community/Pay/Profile use this for a consistent presence.
 * Pushed/detail screens keep the quieter TopBar (back + mark + title).
 */
import { router } from 'expo-router';
import React from 'react';
import { Platform, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { space } from '@bero/tokens';
import { SocietySwitcher } from '@/components/SocietySwitcher';
import { Icon } from '@/features/account-admin/icons';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, IconButton } from '@/ui';

export function BrandBar({ title }: { title?: string }) {
  const { colors } = useTheme();
  const { user } = useSession();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  // On desktop web the persistent sidebar already carries the switcher; here it's
  // the mobile home for it (this bar shows on every tab root on the phone).
  const wide = Platform.OS === 'web' && width >= 1024;
  const initials = (user?.fullName ?? 'R').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <View style={{ paddingTop: insets.top, backgroundColor: colors.canvas0, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.borderSoft }}>
      <View style={{ height: 56, flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.s4, gap: space.s2 }}>
        <Pressable accessibilityRole="link" accessibilityLabel="Bero home" onPress={() => router.push('/home')}>
          <BrandLockup markHeight={22} />
        </Pressable>
        {/* Mobile: society switcher takes the flex space (MyGate-style top selector).
            Desktop: the sidebar has it, so fall back to the optional page title. */}
        {!wide ? (
          <View style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center' }}>
            <SocietySwitcher inline />
          </View>
        ) : title ? (
          <>
            <AppText variant="bodySm" tone="tertiary" style={{ marginLeft: 2 }}>· {title}</AppText>
            <View style={{ flex: 1 }} />
          </>
        ) : (
          <View style={{ flex: 1 }} />
        )}
        <IconButton label="Search" onPress={() => router.push('/account/search')}>
          <Icon name="search" size={19} color={colors.textPrimary} />
        </IconButton>
        <IconButton label="Notifications" onPress={() => router.push('/account/notifications')}>
          <Icon name="bell" size={19} color={colors.textPrimary} />
        </IconButton>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Profile"
          onPress={() => router.push('/profile')}
          style={{
            width: 34, height: 38, borderTopLeftRadius: 17, borderTopRightRadius: 17,
            borderBottomLeftRadius: 5, borderBottomRightRadius: 5,
            backgroundColor: colors.primary100, alignItems: 'center', justifyContent: 'center',
          }}
        >
          <AppText variant="bodySm" weight="700" style={{ color: colors.primary900 }}>{initials}</AppText>
        </Pressable>
      </View>
    </View>
  );
}
